/**
 * Record a repayment (or, when the business borrowed, a payment to the lender).
 *
 * The submission key is made once, when the dialog opens. A double-click, a
 * retry after a dropped connection, or the same form sent twice all carry it,
 * and the API records the payment once.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  compareAmounts,
  loanWording,
  moneySchema,
  type LoanListItem,
  type PaymentMethod,
} from '@cenox/shared';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { Button, Input, Modal, Select, Textarea } from '../../components/ui';
import { newSubmissionKey, useRecordRepayment } from './data';
import { AccountSelect } from '../money/AccountSelect';

export function RepaymentModal({ loan, onClose }: { loan: LoanListItem; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const today = todayIn(business?.timezone ?? 'UTC');
  const words = loanWording(loan.direction);
  const record = useRecordRepayment(loan.id);

  const [key] = useState(newSubmissionKey);
  const [amount, setAmount] = useState(loan.nextDueAmount ?? '');
  const [paidOn, setPaidOn] = useState(today);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [accountId, setAccountId] = useState('');

  const parsed = moneySchema.safeParse(amount);
  const over = parsed.success && compareAmounts(parsed.data, loan.balanceOwed) > 0;
  const positive = parsed.success && compareAmounts(parsed.data, 0) > 0;
  const fieldError = (f: string) => (record.error instanceof ApiError ? record.error.fieldError(f) : undefined);

  // The same payment entered twice under two submissions is refused until
  // somebody says it really is a second payment.
  const looksDuplicate = record.error instanceof ApiError && Boolean(record.error.fieldError('confirmDuplicate'));

  function send(confirmDuplicate: boolean) {
    if (!parsed.success || over || !positive) return;
    record.mutate(
      {
        amount: parsed.data,
        paidOn,
        paymentMethod: method,
        accountId: accountId || null,
        transactionReference: reference.trim() || null,
        notes: notes.trim() || null,
        idempotencyKey: key,
        ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
      },
      { onSuccess: onClose },
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    send(false);
  }

  return (
    <Modal open onClose={onClose} title={words.record}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <dl className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3 text-sm">
          <div>
            <dt className="text-xs text-slate-500">Loan</dt>
            <dd className="font-medium text-slate-900">{loan.reference}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">{words.counterparty}</dt>
            <dd className="truncate text-slate-900">{loan.counterpartyName}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Still owed</dt>
            <dd className="tabular-nums text-slate-900">{money(loan.balanceOwed, loan.currencyCode)}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Next instalment</dt>
            <dd className="tabular-nums text-slate-900">{money(loan.nextDueAmount, loan.currencyCode)}</dd>
          </div>
        </dl>

        <Input
          label={`Amount (${loan.currencyCode})`}
          required
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={
            over
              ? `More than the ${money(loan.balanceOwed, loan.currencyCode)} still owed`
              : fieldError('amount')
          }
          hint="Part payments and early payments are both fine. Interest is settled before principal."
        />
        <button
          type="button"
          className="-mt-2 text-xs font-medium text-brand-700 hover:underline"
          onClick={() => setAmount(loan.balanceOwed)}
        >
          Pay everything owed ({money(loan.balanceOwed, loan.currencyCode)})
        </button>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Payment date" type="date" required value={paidOn} max={today} onChange={(e) => setPaidOn(e.target.value)} error={fieldError('paidOn')} />
          <Select
            label="Payment method"
            value={method}
            onChange={(e) => {
              setMethod(e.target.value as PaymentMethod);
              setAccountId('');
            }}
            options={PAYMENT_METHODS.map((m) => ({ value: m, label: PAYMENT_METHOD_LABELS[m] }))}
          />
        </div>
        <AccountSelect method={method} value={accountId} onChange={setAccountId} error={fieldError('accountId')} />
        <Input label="Transaction reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Bank or transfer reference" />
        <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

        {record.error && !fieldError('amount') && !fieldError('paidOn') && (
          <div role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            <p>{record.error.message}</p>
            {looksDuplicate && (
              <Button type="button" size="sm" variant="secondary" className="mt-2" loading={record.isPending} onClick={() => send(true)}>
                It is a separate payment — record it
              </Button>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={record.isPending} disabled={!positive || over}>
            {words.record}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
